import React from 'react';
import AppRecovery from './AppRecovery';

interface Props {
  /** Friendly route name for diagnostics — e.g. "Account", "Login". */
  label?: string;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
  errorStack: string | null;
}

/**
 * RouteErrorBoundary — per-route render guard.
 *
 * Catches any synchronous render error inside a single <Route /> element
 * (Account, Login, ResetPassword, Privacy, Terms, InviteLanding, Join,
 * AuthCallback, SystemHealth, NotFound, KinsCircleV2Page) and replaces
 * the blank screen with the universal <AppRecovery /> surface.
 *
 * The dashboard (`/`) is already wrapped by <AppBootErrorBoundary> in
 * src/pages/Index.tsx — this boundary covers every OTHER route so no
 * page in the app can render a blank/black/white screen because of a
 * bad mapping, a missing context, or an unexpected null.
 *
 * Pure additive. Does NOT touch:
 *   • spouse / in-law engine
 *   • tree layout
 *   • relationship labels
 *   • Supabase schema / RLS
 *   • billing prices
 *   • Stripe / FamousPay
 *   • homepage copy
 *   • uploads behavior
 *   • invite flow
 *   • existing family data
 */
class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '', errorStack: null };
  }

  static getDerivedStateFromError(err: unknown): State {
    const message =
      err instanceof Error && err.message
        ? err.message
        : 'Unknown render error.';
    const stack =
      err instanceof Error && err.stack ? err.stack.slice(0, 1500) : null;
    return { hasError: true, message, errorStack: stack };
  }

  componentDidCatch(err: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error(
      `[KinsCircle][RouteBoundary:${this.props.label || 'unknown'}] caught render error`,
      {
        err,
        info,
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : '<ssr>',
      },
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: '', errorStack: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const diagnostic = [
      `[${this.props.label || 'route'}] ${this.state.message}`,
      this.state.errorStack ? `\n${this.state.errorStack}` : '',
    ]
      .filter(Boolean)
      .join('');

    return <AppRecovery diagnostic={diagnostic} onRetry={this.handleRetry} />;
  }
}

export default RouteErrorBoundary;
